"use client";

import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { useScopedI18n } from "@/messages/client";
import { Search, ArrowDownToLine, ChevronsUpDown, ArrowUp, ArrowDown, X, FolderUp } from "lucide-react";
import RouterLink from "@/components/ui/router-link";
import { useSearchParams, usePathname } from "next/navigation";
import useSWRMutation from "swr/mutation";
import { fetchCollectionsData, CollectionQuerys } from "@/lib/fetchData";
import Loading from "@/components/ui/loading";
import { useLang, useSite } from "@/hooks/useSiteInfo";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useUser } from "@/components/clients/LocaleToStorage";
import toast from "react-hot-toast";
import Pagination from '@/components/ui/pagination';

type CompetitiveProduct = {
  id: number;
  material_number: string;
  producer: string;
  /** 1:P2P, 2:Function Replacement  */
  type: 1 | 2;
  product: {
    slug: string;
    material_number: string;
    description: string;
    lang: number;
    lang_data: {
      lang: number;
      description: string;
    }[];
    category: {
      name: string;
      lang: number;
      lang_data: {
        lang: number;
        name: string;
      }[];
    };
    application: {
      application_id: {
        name: string;
        lang: number;
        lang_data: {
          lang: number;
          name: string;
        }[];
      };
    }[];
  }
}

const CompetitiveProductTypeMap = {
  1: "P2P",
  2: "Function Replacement",
}

type SortKey = "search" | "resultNumber";

const Sorter = (props: {
  sort: {
    key: SortKey | null,
    direction: "asc" | "desc" | null,
  },
  name: SortKey,
  onChange: (key: SortKey) => void,
}) => {
  return (
    <div className="cursor-pointer" onClick={() => props.onChange(props.name)}>
      {props.sort.key !== props.name && (
        <ChevronsUpDown size={20} className="text-zinc-400" />
      )}
      {props.sort.key === props.name && props.sort.direction === "asc" && (
        <ArrowUp size={20} className="text-primary" />
      )}
      {props.sort.key === props.name && props.sort.direction === "desc" && (
        <ArrowDown size={20} className="text-primary" />
      )}
    </div>
  )

}

const ProducerFilter = (props: {
  producers: string[];
  selectedProducers: string[];
  onSelectedProducersChange: (producers: string[]) => void;
}) => {
  const t = useScopedI18n("交叉搜索");
  const allSelected = useMemo(() => {
    return props.producers.length > 0 && props.producers.every(producer => props.selectedProducers.includes(producer));
  }, [props.producers, props.selectedProducers]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      props.onSelectedProducersChange([]);
    } else {
      props.onSelectedProducersChange([...props.producers]);
    }
  }, [allSelected, props.producers, props.onSelectedProducersChange]);

  return (
    <div className="p-4 bg-white rounded-lg shadow-lg">
      <div className="flex items-center mb-4">
        <Checkbox
          checked={allSelected}
          onCheckedChange={handleSelectAll}
        />
        <span className="text-sm font-medium text-black ml-2">{t("全选")}</span>
      </div>
      <div className="space-y-2">
        {props.producers.map((producer) => (
          <div key={producer} className="flex items-center space-x-2">
            <Checkbox
              checked={props.selectedProducers.includes(producer)}
              onCheckedChange={(checked) => {
                if (checked) {
                  props.onSelectedProducersChange([...props.selectedProducers, producer]);
                } else {
                  props.onSelectedProducersChange(props.selectedProducers.filter(p => p !== producer));
                }
              }}
            />
            <span className="text-sm text-black">{producer}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function CrossReference(props: {
  data: {
    title: string;
    description: string;
    notice: string;
  }
}) {
  const lang = useLang();
  const site = useSite();
  const locale = useCurrentLocale();
  const t = useScopedI18n("交叉搜索")
  const { user } = useUser();
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const [materialNumber, setMaterialNumber] = useState("");
  const searchMaterialNumber = useRef<string>();
  const [allMultiFilters, setAllMultiFilters] = useState<{
    search: string;
  }[]>([]);
  const [multiFilters, setMultiFilters] = useState<{
    search: string;
    checked: boolean;
  }[]>([]);

  const [multiFilterSort, setMultiFilterSort] = useState<{
    key: SortKey | null,
    direction: "asc" | "desc" | null,
  }>({
    key: null,
    direction: null,
  });

  const selectedMultiFilters = useMemo(() => {
    return multiFilters.filter((el) => el.checked).map((el) => el.search);
  }, [multiFilters]);

  const changeFilterSort = useCallback((key: SortKey) => { 
    setMultiFilterSort((prev) => {
      if (prev.key !== key) {
        return {
          key,
          direction: "asc",
        }
      } else {
        if (prev.direction === "asc") {
          return {
            ...prev,
            direction: "desc",
          }
        } else {
          return {
            key: null,
            direction: null,
          }
        }
      }
    })
  }, []);

  const filterCheckAll = useMemo(
    () => !multiFilters.find((el) => !el.checked),
    [multiFilters]);

  const changeFilterCheck = useCallback((index: number) => {
    setMultiFilters((prev) => {
      return prev.map((item, i) => ({
        ...item,
        checked: i === index ? !item.checked : item.checked,
      }))
    });
  }, []);

  const changeFilterCheckAll = useCallback(() => {
    setMultiFilters((prev) => {
      return prev.map((item) => ({
        ...item,
        checked: !filterCheckAll,
      }))
    });
  }, [filterCheckAll]);

  const {
    trigger,
    isMutating: loading,
    data: result,
    reset: resetResult,
  } = useSWRMutation(
    [
      "/items/competitive_product",
      allMultiFilters,
    ],
    async (arg) => {
      const [, allMultiFilters] = arg;

      if (searchMaterialNumber.current || allMultiFilters.length > 0) {
        const querys: CollectionQuerys = [];
        if (searchMaterialNumber.current) {
          querys.push({
            key: "material_number",
            filter: {
              _contains: searchMaterialNumber.current,
            }
          });
        } else if (allMultiFilters.length > 0) {
          querys.push({
            key: "_or",
            filter: allMultiFilters.map((el) => ({
              key: ["material_number", "_contains"],
              value: el.search,
            })),
          });
        }
        
        const res = await fetchCollectionsData<CompetitiveProduct>("competitive_product", {
          page: 1,
          limit: 1000,
          publish: false,
          fields: [
            "id",
            "material_number",
            "producer",
            "type",
            "product.slug",
            "product.material_number",
            "product.category.name",
            "product.categoy.lang",
            "product.category.lang_data.lang",
            "product.category.lang_data.name",
            "product.description",
            "product.lang",
            "product.lang_data.lang",
            "product.lang_data.description",
            "product.application.application_id.name",
            "product.application.application_id.lang",
            "product.application.application_id.lang_data.lang",
            "product.application.application_id.lang_data.name",
          ],
          querys: [
            ...querys,
            {
              key: ["product", "publish", "publish_id", "status"],
              value: "PUBLISH"
            },
            {
              key: ["product", "publish", "publish_id", "site", "id"],
              value: String(site),
            }
          ],
          sort: "material_number",
        });

        setSelectedProducers([]);

        setSearched(true);

        return res;
      }
    }
  );

  const multiFilterMap = useMemo(() => {
    return multiFilters.map((el) => ({
      search: el.search,
      checked: el.checked,
      resultNumber: result?.data?.filter((item) => item.material_number.includes(el.search)).length ?? 0,
    })).sort((prev, next) => {
      if (multiFilterSort.key === "search") {
        return multiFilterSort.direction === "asc" ? prev.search.localeCompare(next.search) : next.search.localeCompare(prev.search);
      } else if (multiFilterSort.key === "resultNumber") {
        return multiFilterSort.direction === "asc" ? prev.resultNumber - next.resultNumber : next.resultNumber - prev.resultNumber;
      }

      return 0;
    })
  }, [multiFilters, result, multiFilterSort]);

  const [selectedProducers, setSelectedProducers] = useState<string[]>([]);

  const producerMap = useMemo(() => {
    const producerSet = new Set<string>();
    result?.data?.forEach((item) => {
      if (item.producer) {
        producerSet.add(item.producer);
      }
    });

    return Array.from(producerSet);
  }, [result]);

  const filterResult = useMemo(() => {
    const langData = result?.data?.map((item) => {
      const productLangData = item.product.lang_data.find((el) => el.lang === lang);
      const productCategoryLangData = item.product.category.lang_data.find((el) => el.lang === lang);
      return {
        ...item,
        product: {
          ...item.product,
          ...productLangData,
          category: {
            ...item.product.category,
            ...productCategoryLangData,
          },
          application: item.product.application.map((el) => {
            const applicationLangData = el.application_id.lang_data.find((el) => el.lang === lang);
            return {
              application_id: {
                ...el.application_id,
                ...applicationLangData,
              }
            }
          })
        }

      }
    }) ?? [];

    return langData.filter((item) => {
      let match = true;
      if (multiFilters.length > 0) {
        if (!selectedMultiFilters.some((el) => item.material_number.includes(el))) {
          match = false;
        }
      }
      if (selectedProducers.length > 0) {
        if (!selectedProducers.some((el) => item.producer === el)) {
          match = false;
        }
      }

      return match;
    })
  }, [result, lang, selectedProducers, selectedMultiFilters, multiFilters]);


  const reset = useCallback(() => {
    setMaterialNumber("");
    resetResult();
    searchMaterialNumber.current = "";
  }, [resetResult]);

  const pathname = usePathname();

  const searchParams = useSearchParams();

  const redirect_url = useMemo(() => {
    if (pathname.includes("/login")) {
      return "";
    }

    const query = searchParams.toString();

    return encodeURI(`${pathname}${query ? `?${query}` : ""}`);
  }, [pathname, searchParams]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { trigger: uploadTrigger, isMutating: isUploading } = useSWRMutation(
    [
      "/api/competitiveProduct/batch/params",
    ],
    async (_, { arg }: { arg: File }) => {
      if (!arg) return;

      const formData = new FormData();
      formData.append('file', arg);

      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_HOST}/competitiveProduct/batch/params`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          const filters: { search: string }[] = (data?.data ?? []).map(
            (item: {
              material_number: string;
            }) => ({ search: item.material_number })
          )
          setMultiFilters(filters.map((el) => ({ ...el, checked: false })));
          setAllMultiFilters(filters);
          setSearched(true);
          setOpen(false);
        }
      } catch (error) {
        toast.error(t("上传失败"));
      }
    }
  )

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    uploadTrigger(file)
  }, [uploadTrigger]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleSingleSearch = useCallback(() => {
    setMultiFilters([]);
    setAllMultiFilters([]);
    searchMaterialNumber.current = materialNumber;
    trigger();
  }, [trigger, materialNumber]);

  const cancelSearch = () => {
    setTimeout(() => {
      setMaterialNumber(searchMaterialNumber.current || "");
    }, 100);
  };

  useEffect(() => {
    if (allMultiFilters.length > 0) {
      setMaterialNumber("");
      searchMaterialNumber.current = "";
      trigger();
    }
  }, [allMultiFilters]);

  const { trigger: exportTrigger, isMutating: isExporting } = useSWRMutation(
    [
      "/api/competitiveProduct/batch/select/export",
      allMultiFilters,
      site,
    ],
    async ([, allMultiFilters, site]) => {
      try {
        const materialNumbers: { material_number: string }[] = [];
        if (searchMaterialNumber.current) {
          materialNumbers.push({ material_number: searchMaterialNumber.current });
        } else if (allMultiFilters.length > 0) {
          materialNumbers.push(...allMultiFilters.map((el) => ({ material_number: el.search })));
        }
        if (!materialNumbers.length) {
          return;
        }
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_HOST}/competitiveProduct/batch/select/export?site=${site}`, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            material_numbers: materialNumbers,
          }),
        });
  
        if (response.ok) {
          if (
            response.headers.get("Content-Type") ===
            "application/vnd.ms-excel;charset=utf-8"
          ) {
            const fileName = response.headers
              .get("Content-Disposition")
              ?.split(";")[1]
              ?.split("=")[1];
  
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download =
              fileName ??
              `cross-reference-${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        } else {
          toast.error(t("导出失败"));
        }
      } catch (error) {
        toast.error(t("导出失败"));
      }
    }
  )

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const pagedFilterResult = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filterResult.slice(start, start + pageSize);
  }, [filterResult, page]);

  useEffect(() => {
    setPage(1);
  }, [result, selectedMultiFilters, selectedProducers]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center pt-8 md:pt-14 pb-16 md:pb-32 px-4 md:px-0">
      {/* 标题部分 */}
      <div className="text-center mb-10 md:mb-20">
        <h1 className="text-2xl md:text-5xl leading-normal mb-3 md:mb-4 text-zinc-700">{props.data.title}</h1>
        <p className="text-sm md:text-2xl text-zinc-700">{props.data.description}</p>
      </div>

      {
        !searched && <>
          {/* 搜索卡片部分 */}
          <div className="flex flex-col md:flex-row gap-4 md:gap-6 w-full max-w-7xl">
            {/* 单器件搜索 */}
            <div className="flex-1 bg-zinc-50 rounded-2xl md:rounded-3xl shadow p-4 md:p-8 flex flex-col justify-between md:h-[380px]">
              <div className="flex justify-between">
                <div className="mt-2 md:mt-3">
                  <h2 className="text-lg md:text-3xl mb-1 md:mb-2">{t("单器件搜索")}</h2>
                  <p className="text-sm md:text-lg text-zinc-800 mb-4 md:mb-6">{t("单器件搜索提示")}</p>
                </div>
                <Image src="/cross-reference-bg.png" alt="single search" width={274} height={160} className="hidden md:block" />
              </div>
              <div className="group relative mt-2 md:mt-3 w-full text-xs md:text-sm">
                <input
                  type="text"
                  className="w-full rounded-lg border py-2 md:py-2.5 pl-4 md:pl-5 pr-4 outline-none placeholder:text-zinc-200 focus:border-primary group-hover:border-primary"
                  value={materialNumber}
                  onChange={(e) => {
                    setMaterialNumber(e.target.value)
                  }}
                  placeholder={t("搜索提示")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSingleSearch();
                    }
                  }}
                  onBlur={cancelSearch}
                />
                <Button
                  variant="link"
                  onClick={() => {
                    handleSingleSearch();
                  }}
                  disabled={!materialNumber}
                  className="absolute right-0 top-1/2 -translate-y-1/2"
                >
                  <Search color="#000000" className="h-4 w-4 md:h-5 md:w-5" size={18} />
                </Button>
              </div>
              <div className="flex gap-2 md:gap-3 mt-4 md:mt-0">
                <Button
                  size="sm"
                  variant="primary"
                  type="button"
                  onClick={handleSingleSearch}
                  disabled={!materialNumber}
                  className="px-4 md:px-6"
                >{t("搜索")}</Button>
                <Button
                  size="sm"
                  variant="neutral"
                  type="button"
                  className="px-4 md:px-6"
                  onClick={reset}
                >{t("重置")}</Button>
              </div>
            </div>

            {/* 多器件批量搜索 */}
            <div className="flex-1 bg-zinc-50 rounded-2xl md:rounded-3xl shadow p-4 md:p-8 flex flex-col justify-between md:h-[380px]">
              <div>
                <div className="flex justify-between">
                  <h2 className="text-lg md:text-3xl mb-1 md:mb-2">{t("多器件批量搜索")}</h2>
                  {!user && (
                    <RouterLink href={`/login?redirect_url=${redirect_url}`}>
                      <span className="text-default text-sm md:text-base">{t("请登录")}</span>
                      <span className="text-primary text-sm md:text-base"> myPQ </span>
                    </RouterLink>
                  )}
                </div>
                <p className="text-sm md:text-lg text-zinc-800 mb-6 md:mb-12">
                  {t("多器件批量搜索提示")}
                  <br />
                  <span className="flex items-center">
                    {t("支持的格式")}
                    <span className="flex items-center gap-x-3 md:gap-x-5">
                      <Image className="w-5 md:w-7" src="/icons/csv.png" alt="csv" width={28} height={28} />
                      <Image className="w-5 md:w-7" src="/icons/tsv.png" alt="tsv" width={28} height={28} />
                      <Image className="w-5 md:w-7" src="/icons/xls.png" alt="xls" width={28} height={28} />
                      <Image className="w-5 md:w-7" src="/icons/xlsx.png" alt="xlsx" width={28} height={28} />
                    </span>
                  </span>
                </p>
                <Button
                  variant="default"
                  type="button"
                  className="w-full text-xs md:text-sm py-2 md:py-2.5 rounded-lg"
                  onClick={handleUploadClick}
                  disabled={!user}
                  loading={isUploading}
                >
                  <FolderUp className="mr-1 md:mr-1.5 h-4 w-4 md:h-5 md:w-5" size={18} />
                  {t("上传文件")}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".csv,.tsv,.xls,.xlsx"
                    className="hidden"
                  />
                </Button>
                <a
                  href={
                    locale === "en" ? "/templates/bacth-search-template-en.xlsx" : "/templates/bacth-search-template.xlsx"
                  }
                  download={
                    locale === "en" ? "bacth-search-template.xlsx" : "批量搜索模板.xlsx"
                  }
                  target="_blank"
                  className="flex items-center text-teal-600 mt-3 md:mt-4 text-xs md:text-sm hover:underline"
                >
                  <ArrowDownToLine className="mr-1 md:mr-1.5 h-4 w-4 md:h-5 md:w-5" size={18} />
                  {t("下载模板")}
                </a>
              </div>
            </div>
          </div>

          {/* 免责声明部分 */}
          <div className="w-full max-w-7xl mt-12 md:mt-24 bg-white">
            <div className="flex items-center mb-2 md:mb-3">
              <img className="mr-2 w-4 md:w-6" src="/icons/statement.svg" alt="Icon Statement" />
              <span className="font-semibold text-lg md:text-2xl">{t("重要声明")}</span>
            </div>
            <p className="pl-6 md:pl-8 whitespace-pre-line text-zinc-800 text-xs md:text-sm leading-relaxed">
              {props.data.notice}
            </p>
          </div>
        </>
      }

      {
        searched && <>
          <div className="flex flex-col md:flex-row items-start md:items-center w-full max-md:gap-3">
            <span className="flex items-center shrink-0 text-lg md:text-2xl font-medium">
              <img className="mr-1 w-4 md:w-6" src="/icons/update.svg" alt="Icon Update" />
              {t("更新搜索")}
            </span>
            <input
              type="text"
              className="w-full md:grow md:ml-7 md:mr-12 rounded-lg border py-1.5 px-4 md:px-8 outline-none placeholder:text-zinc-200 focus:border-primary group-hover:border-primary text-sm md:text-base"
              value={materialNumber}
              onChange={(e) => {
                setMaterialNumber(e.target.value)
              }}
              placeholder={t("搜索提示")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSingleSearch();
                }
              }}
              onBlur={cancelSearch}
            />
            <div className="flex gap-4 md:gap-8 w-full md:w-auto">
              <Button
                size="sm"
                variant="primary"
                type="button"
                onClick={() => {
                  handleSingleSearch();
                }}
                disabled={!materialNumber}
                className="flex-1 md:flex-none px-4 md:px-8"
              >{t("搜索")}</Button>
              
              <Button
                size="sm"
                variant="neutral"
                type="button"
                className="flex-1 md:flex-none px-4 md:px-8"
                onClick={reset}
              >{t("重置")}</Button>

              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="neutral"
                    type="button"
                    className="flex-1 md:flex-none px-4 md:px-8"
                  >{t("批量搜索")}</Button>
                </DialogTrigger>
                <DialogPortal>
                  <DialogContent className="w-[95%] md:min-w-[680px] flex flex-col px-4 md:px-9 bg-white border-none shadow">
                    <DialogTitle className="flex items-center text-base md:text-lg font-medium">
                      <img className="mr-1 w-4 md:w-6" src="/icons/multiple-search.svg" alt="Icon Multiple Search" />
                      {t("多器件批量搜索")}
                    </DialogTitle>
                    <div className="px-y my-6 md:my-11">
                      <Button
                        variant="outline"
                        type="button"
                        className="flex items-center w-full text-xs md:text-sm py-2 md:py-3 rounded-2xl md:rounded-3xl"
                        disabled={!user}
                        onClick={handleUploadClick}
                        loading={isUploading}
                      >
                        <FolderUp className="mr-1 md:mr-1.5 h-4 w-4 md:h-5 md:w-5" size={18} />
                        {t("上传文件")}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          accept=".csv,.tsv,.xls,.xlsx"
                          className="hidden"
                        />
                      </Button>
                      <p className="text-xs md:text-sm text-zinc-700 mt-4 md:mt-7 leading-6">
                        {t("多器件批量搜索提示")} {t("支持的格式")}
                        <span className="inline-flex items-center gap-x-3 md:gap-x-4 translate-y-2">
                          <Image className="w-5 md:w-6" src="/icons/csv.png" alt="csv" width={24} height={24} />
                          <Image className="w-5 md:w-6" src="/icons/tsv.png" alt="tsv" width={24} height={24} />
                          <Image className="w-5 md:w-6" src="/icons/xls.png" alt="xls" width={24} height={24} />
                          <Image className="w-5 md:w-6" src="/icons/xlsx.png" alt="xlsx" width={24} height={24} />
                        </span>
                      </p>
                      <div className="mt-8 md:mt-16 flex justify-between items-center">
                        <a 
                          href={
                            locale === "en" ? "/templates/bacth-search-template-en.xlsx" : "/templates/bacth-search-template.xlsx"
                          }
                          download={
                            locale === "en" ? "bacth-search-template.xlsx" : "批量搜索模板.xlsx"
                          }
                          target="_blank"
                          className="flex items-center text-teal-600 text-xs md:text-sm hover:underline"
                        >
                          <ArrowDownToLine className="mr-1 md:mr-1.5 h-4 w-4 md:h-5 md:w-5" size={18} />
                          {t("下载模板")}
                        </a>
                        {!user && (
                          <RouterLink className="text-xs md:text-sm" href={`/login?redirect_url=${redirect_url}`}>
                            <span className="text-default">{t("请登录")}</span>
                            <span className="text-primary"> myPQ </span>
                          </RouterLink>
                        )}
                      </div>
                    </div>
                    <DialogClose className="absolute right-2 md:right-4 top-4 md:top-8 p-2 text-white">
                      <X size={24} className="md:hidden" />
                      <X size={30} className="hidden md:block" />
                    </DialogClose>
                  </DialogContent>
                </DialogPortal>
              </Dialog>
            </div>
          </div>
          <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-center mt-8 md:mt-16 gap-4 md:gap-0">
            <span className="flex items-center text-lg md:text-2xl font-medium">
              <img className="mr-1 w-4 md:w-6" src="/icons/result.svg" alt="Icon Result" />
              {t("搜索结果")}
            </span>
            
            <Button
              variant="link"
              className="flex items-center text-[#17A29E] hover:no-underline hover:opacity-80 text-sm md:text-base"
              onClick={() => {
                exportTrigger();
              }}
              disabled={!result?.data.length}
              loading={isExporting}
            >
              <img className="mr-2 md:mr-2.5 w-4 md:w-6" src="/icons/export.svg" alt="Icon Export" />
              {t("导出全部搜索结果")}
            </Button>
          </div>

          {(result?.data?.length ?? 0) > 0 && <>
            {/* 第一个表格 */}
            {multiFilters.length > 0 && (
              <div className="w-full mt-4 md:mt-5 mb-3 md:mb-11 bg-white rounded-lg md:rounded-xl shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left md:min-w-[600px]">
                    <thead className="bg-zinc-50">
                      <tr className="text-xs md:text-sm">
                        <th className="px-4 md:px-6 py-2 md:py-3 font-medium text-zinc-700">
                          <Checkbox
                            checked={filterCheckAll}
                            onCheckedChange={changeFilterCheckAll}
                          />
                        </th>
                        <th className="px-4 md:px-6 py-2 md:py-3 font-medium text-zinc-700 items-center gap-1">
                          <span className="flex items-center gap-x-1.5">
                            {t("搜索的器件")}
                            <Sorter
                              sort={multiFilterSort}
                              name="search"
                              onChange={changeFilterSort}
                            />
                          </span>
                        </th>
                        <th className="px-4 md:px-6 py-2 md:py-3 font-medium text-zinc-700 items-center gap-1">
                          <span className="flex items-center gap-x-1.5">
                            {t("结果")}
                            <Sorter
                              sort={multiFilterSort}
                              name="resultNumber"
                              onChange={changeFilterSort}
                            />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {multiFilterMap.map((el, index) => (
                        <tr key={el.search} className="border-b border-[#2C2C2C] bg-[#E8F3F5] text-xs md:text-sm">
                          <td className="px-4 md:px-6 py-2 md:py-3">
                            <Checkbox
                              checked={el.checked}
                              onCheckedChange={() => changeFilterCheck(index)}
                            />
                          </td>
                          <td className="px-4 md:px-6 py-2 md:py-3">{el.search}</td>
                          <td className="px-4 md:px-6 py-2 md:py-3 text-teal-600 cursor-pointer">
                            {t<any, any>("匹配项数", { count: el.resultNumber })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p className="w-full mt-5 text-base md:text-xl text-zinc-700">{t("查看器件以选择匹配项")}</p>

            {/* 第二个表格，支持横向滚动 */}
            <div className="w-full mt-4 md:mt-5 overflow-x-auto">
              <table className="min-w-[900px] w-full text-left">
                <thead>
                  <tr className="text-white text-sm md:text-lg font-medium">
                    <th className="px-6 md:px-11 py-2 md:py-3 bg-primary whitespace-nowrap border-r-4 border-white">
                      {t("兼容料号")}
                    </th>
                    <th className="px-6 md:px-11 py-2 md:py-3 bg-primary whitespace-nowrap border-r-4 border-white">
                      <div className="flex items-center">
                        {t("制造商")}
                        <Popover>
                          <PopoverTrigger asChild>
                            <img
                              src="/icons/filter.svg"
                              alt="Producer Filter"
                              className="inline w-3 h-3 md:w-4 md:h-4 ml-4 md:ml-8 cursor-pointer"
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-52 p-0 border-none">
                            <ProducerFilter
                              producers={producerMap}
                              selectedProducers={selectedProducers}
                              onSelectedProducersChange={setSelectedProducers}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </th>
                    <th className="px-6 md:px-11 py-2 md:py-3 bg-primary whitespace-nowrap border-r-4 border-white">
                      {t("POWERQUARK产品型号")}
                    </th>
                    <th className="px-6 md:px-11 py-2 md:py-3 bg-primary whitespace-nowrap border-r-4 border-white">
                      {t("可供替代类别")}
                    </th>
                    <th className="px-6 md:px-11 py-2 md:py-3 bg-primary whitespace-nowrap border-r-4 border-white">
                      {t("产品类别")}
                    </th>
                    <th className="px-6 md:px-11 py-2 md:py-3 bg-primary whitespace-nowrap border-r-4 border-white">
                      {t("应用场景")}
                    </th>
                    <th className="px-6 md:px-11 py-2 md:py-3 bg-primary whitespace-nowrap">
                      {t("产品描述")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFilterResult.map((item) => 
                    <tr key={item.id} className="border-b hover:bg-zinc-50 text-xs md:text-sm">
                      <td className="px-6 md:px-11 py-2 md:py-3 whitespace-nowrap">{item.material_number}</td>
                      <td className="px-6 md:px-11 py-2 md:py-3 whitespace-nowrap">{item.producer}</td>
                      <td className="px-6 md:px-11 py-2 md:py-3 whitespace-nowrap">
                        <RouterLink
                          className="text-[#17A29E] hover:text-[#17A29E] hover:opacity-80"
                          target="_blank"
                          href={`/product/${item.product.slug}`}
                        >{item.product.material_number}</RouterLink>
                      </td>
                      <td className="px-6 md:px-11 py-2 md:py-3 whitespace-nowrap">{CompetitiveProductTypeMap[item.type]}</td>
                      <td className="px-6 md:px-11 py-2 md:py-3 whitespace-nowrap">{item.product.category.name}</td>
                      <td className="px-6 md:px-11 py-2 md:py-3 whitespace-nowrap">
                        {item.product.application.map((el) => el.application_id.name).join("、")}
                      </td>
                      <td className="px-6 md:px-11 py-2 md:py-3">
                        <p
                          className="w-[200px] md:w-[500px] text-ellipsis line-clamp-2"
                          title={item.product.description}
                        >{item.product.description}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              className="mt-4 mb-8"
              total={filterResult.length}
              pageSize={pageSize}
              current={page}
              onChange={setPage}
              mode="client"
            />
          </>}

          {!result?.data?.length && (
            <div className="mt-2.5 flex w-full flex-col items-center py-10 md:mt-10 md:py-20">
              <img
                className="w-40 md:w-[270px]"
                src="/no-filter.png"
                alt={t("未检索到相关数据")}
              />
              <span className="mt-3 text-sm text-gray md:mt-5">
                {t("未检索到相关数据")}
              </span>
            </div>
          )}
        </>
      }
      {loading && (
        <Loading
          loading
          className="fixed left-0 top-0 h-[100vh] w-[100vw]"
          hideText
        />
      )}
    </div>
  );
};